import React, { useState, useEffect } from "react";
import { Table, Button, Modal, message, Input, Card, Alert } from "antd";
import axios from "axios";
import JourneyForm from "./journeyForm";
import {
  EditOutlined,
  DeleteOutlined,
  BarsOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import CommonPagination from "../../components/common/pagination";

const Journey = () => {
  const [allJourney, setAllJourney] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [open, setOpen] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchYear, setSearchYear] = useState("");
  const [sortYear, setSortYear] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [successMsg, setSuccessMsg] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const fieldLabels = {
    year: "Year",
    title_id: "Title ID",
    title_en: "Title EN",
    description_id: "Description ID",
    description_en: "Description EN",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const fetchJourney = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/journey`, {
        params: {
          limit: 1000,
        },
      });

      if (!Array.isArray(res.data.data)) {
        throw new Error("Response data.data is not an array");
      }

      setAllJourney(res.data.data);
    } catch (err) {
      console.error("Error fetching journey:", err.response || err.message);
      message.error("Failed to fetch journey");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJourney();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchText, searchYear, sortYear]);

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(`${BASE_URL}/api/journey/${editData.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSuccessMsg("Journey updated");
      } else {
        await axios.post(`${BASE_URL}/api/journey`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSuccessMsg("Journey Added");
      }

      setTimeout(() => setSuccessMsg(null), 3000);
      setOpen(false);
      setEditData(null);
      fetchJourney();
    } catch (err) {
      message.error("Failed to submit journey");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/journey/${id}`);
      setSuccessMsg("Journey deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchJourney();
    } catch (err) {
      message.error("Failed to delete");
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("id-ID", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const filteredAndSortedJourney = [...allJourney]
    .filter((item) => {
      const matchYear = searchYear
        ? String(item.year).toLowerCase().includes(searchYear.toLowerCase())
        : true;

      const matchTitle = searchText
        ? item.title_id?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.title_en?.toLowerCase().includes(searchText.toLowerCase())
        : true;

      return matchYear && matchTitle;
    })
    .sort((a, b) => {
      if (sortYear === "asc") {
        return Number(a.year) - Number(b.year);
      }
      return Number(b.year) - Number(a.year);
    });

  const paginatedJourney = filteredAndSortedJourney.slice(
    (page - 1) * limit,
    page * limit,
  );

  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            userSelect: "none",
            whiteSpace: "nowrap", // ✅ fix supaya ga turun
          }}
        >
          Year {sortYear === "asc" ? "↑" : "↓"}
        </span>
      ),
      dataIndex: "year",
      key: "year",
      onHeaderCell: () => ({
        onClick: () => {
          setSortYear((prev) => (prev === "asc" ? "desc" : "asc"));
        },
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (value) =>
        value ? (
          <div
            style={{
              position: "relative",
              width: 60,
              height: 60,
              borderRadius: 8,
              overflow: "hidden",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              const overlay = e.currentTarget.querySelector(".overlay");
              if (overlay) overlay.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              const overlay = e.currentTarget.querySelector(".overlay");
              if (overlay) overlay.style.opacity = 0;
            }}
          >
            <img
              src={`${BASE_URL}/uploads/${value}`}
              alt="journey"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />

            {/* Overlay + Eye */}
            <div
              className="overlay"
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "0.3s ease",
              }}
            >
              <EyeOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage(`${BASE_URL}/uploads/${value}`);
                  setPreviewOpen(true);
                }}
                style={{
                  fontSize: 18,
                  color: "#fff",
                  background: "rgba(0,0,0,0.6)",
                  padding: 6,
                  borderRadius: "50%",
                }}
              />
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    { title: "Title ID", dataIndex: "title_id" },
    { title: "Title EN", dataIndex: "title_en" },
    {
      title: "Actions",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Button
            style={{
              borderColor: "#ec4899",
              color: "#ec4899",
              marginRight: 8,
            }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData(record);
              setOpenDetail(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData(record);
              setOpen(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#374151",
              borderColor: "#374151",
              color: "#fff",
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to delete this journey?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="Timeline">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
            backgroundColor: "#f6ffed",
            borderColor: "#f6ffed",
            color: "#389e0d",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Input
            placeholder="Search By Year"
            value={searchYear}
            onChange={(e) => setSearchYear(e.target.value)}
            style={{ width: 150 }}
            allowClear
          />
          <Input.Search
            placeholder="Search Journey..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </div>

        <Button
          style={{
            backgroundColor: "#ec4899",
            borderColor: "#ec4899",
            color: "#fff",
          }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add Journey
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={paginatedJourney}
        rowKey="id"
        loading={loading}
        pagination={false}
        style={{ marginTop: 20 }}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={filteredAndSortedJourney.length}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        destroyOnClose
      >
        <JourneyForm
          initialValues={editData}
          onSubmit={handleSubmit}
          editMode={!!editData}
        />
      </Modal>

      <Modal
        title="Timeline Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={null}
      >
        {detailData && (
          <Table
            dataSource={Object.entries(detailData)
              .filter(([key]) => key !== "id")
              .map(([key, value]) => ({
                key,
                field: fieldLabels[key] || key,
                value:
                  key === "image" && value ? (
                    Array.isArray(value) ? (
                      value.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.url ? img.url : `${BASE_URL}${img}`}
                          alt={img.name || "banner"}
                          style={{ width: 100, marginRight: 8 }}
                        />
                      ))
                    ) : (
                      <div
                        style={{
                          position: "relative",
                          width: 80,
                          height: 100,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={`${BASE_URL}/uploads/${value}`}
                          alt="banner"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />

                        <EyeOutlined
                          onClick={() => {
                            setPreviewImage(`${BASE_URL}/uploads/${value}`);
                            setPreviewOpen(true);
                          }}
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: 20,
                            color: "#fff",
                            background: "rgba(0,0,0,0.5)",
                            padding: 6,
                            borderRadius: "50%",
                            cursor: "pointer",
                          }}
                        />
                      </div>
                    )
                  ) : key === "created_at" || key === "updated_at" ? (
                    value ? (
                      formatDate(value)
                    ) : (
                      "-"
                    )
                  ) : (
                    value?.toString()
                  ),
              }))}
            columns={[
              {
                title: "Field",
                dataIndex: "field",
                key: "field",
                width: "30%",
                render: (text) => <strong>{text}</strong>,
              },
              {
                title: "Value",
                dataIndex: "value",
                key: "value",
              },
            ]}
            pagination={false}
            bordered
          />
        )}
      </Modal>

      <Modal
        open={previewOpen}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        centered
        width="auto"
        closeIcon={
          <div
            style={{
              background: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              padding: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloseOutlined style={{ color: "#fff", fontSize: 14 }} />
          </div>
        }
        styles={{
          content: {
            background: "transparent",
            boxShadow: "none",
          },
          body: {
            background: "transparent",
            padding: 0,
            textAlign: "center",
          },
        }}
      >
        <img
          src={previewImage}
          alt="preview"
          style={{
            maxWidth: "90vw",
            maxHeight: "80vh",
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      </Modal>
    </Card>
  );
};

export default Journey;
